/**
 * Hook to fetch specific facet options by their IDs.
 * Used to resolve labels for selected filter values that aren't in the current paginated results.
 */

import { useQuery } from '@tanstack/react-query';
import i18n from 'i18next';

import type { FacetOption } from './types';
import { useTypesenseAPIKey } from './useTypesenseAPIKey';
import { typesense } from '@/api/typesense/typesenseClient';
import type { ResourceConfig } from '@/lib/resource-config';

interface UseFacetOptionsByIdsParams {
  config: ResourceConfig;
  facetKey: string;
  ids: string[];
}

/**
 * Fetches specific facet options by their IDs.
 * Only works for collection-based value sources.
 *
 * @param params - Hook parameters
 * @returns React Query result with requested options
 */
export function useFacetOptionsByIds({
  config,
  facetKey,
  ids,
}: UseFacetOptionsByIdsParams) {
  const facetConfig = config.facets[facetKey];

  // Get API key for the options collection
  // Use apiKeyName override if specified, otherwise use collection name
  const collectionName =
    facetConfig?.valueSource.kind === 'collection'
      ? facetConfig.valueSource.apiKeyName || facetConfig.valueSource.collection
      : '';
  const { apiKey } = useTypesenseAPIKey(collectionName);

  // Build query key
  const sortedIds = [...ids].sort(); // Sort for cache stability without mutating input
  const queryKey = [
    'facetOptionsByIds',
    config.resourceKey,
    facetKey,
    collectionName,
    ...sortedIds,
  ];

  const queryResult = useQuery<FacetOption[]>({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey,
    queryFn: async () => {
      if (!facetConfig || facetConfig.valueSource.kind !== 'collection') {
        return [];
      }

      if (ids.length === 0) {
        return [];
      }

      if (!apiKey) {
        console.error('[useFacetOptionsByIds] API key not available');
        return [];
      }

      const { valueSource } = facetConfig;
      const {
        collection,
        idField,
        slugField,
        labelField,
        sortField,
        translateLabels,
        translationNamespace,
      } = valueSource;

      try {
        // Build filter query: SourceId:=[123,456,789]
        const filterBy = `${idField}:=[${ids.join(',')}]`;

        const searchRequest = {
          searches: [
            {
              collection,
              'x-typesense-api-key': apiKey,
              q: '*', // Match all (filter handles selection)
              filter_by: filterBy,
              per_page: ids.length, // Fetch all requested IDs
              page: 1,
              sort_by: sortField ? `${sortField}:asc` : `${labelField}:asc`,
            },
          ],
        };

        const multiSearchResults =
          await typesense.multiSearch.perform(searchRequest);
        const result = multiSearchResults.results[0] as {
          hits?: Array<{ document: Record<string, unknown> }>;
        };

        const options: FacetOption[] = (result.hits || []).map((hit) => {
          const rawLabel = String(hit.document[labelField] || '').trim();

          // Apply translation if translateLabels flag is set
          let label = rawLabel;
          if (translateLabels && rawLabel) {
            // Use translationNamespace if specified, otherwise use resourceKey
            const namespace = translationNamespace || config.resourceKey;
            const enumTranslationKey = `enums.${rawLabel}`;
            if (
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              i18n.exists(enumTranslationKey, { ns: namespace as any })
            ) {
              label = i18n.t(enumTranslationKey, {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                ns: namespace as any,
              });
            }
          }

          return {
            id: String(hit.document[idField]),
            slug: String(hit.document[slugField]),
            label,
          };
        });

        return options;
      } catch (error) {
        console.error(
          `[useFacetOptionsByIds] Error fetching options for ${collection}:`,
          error,
        );
        return [];
      }
    },
    staleTime: 10 * 60 * 1000, // 10 minutes (IDs are stable)
    gcTime: 15 * 60 * 1000, // 15 minutes
    enabled:
      !!facetConfig &&
      facetConfig.valueSource.kind === 'collection' &&
      !!apiKey &&
      ids.length > 0,
  });

  return queryResult;
}
